import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, MapPin, ThumbsDown, ThumbsUp } from "lucide-react";
import type { CommunityTag } from "@/api/community";
import type { LngLat } from "@/utils/polyline";
import { nearestPointOnPolyline } from "@/utils/polyline";

interface Props {
  tags: CommunityTag[];
  /** Coordinates of the currently selected route. Tags within `bufferM` of
   *  this polyline are listed in order of how far along they are. */
  routeCoords: LngLat[];
  /** Buffer in meters — only show tags within this distance of the route. */
  bufferM?: number;
  /** Tap a row → host pans the map and opens that signal's popup. */
  onFocus: (tagId: string) => void;
}

function fmtAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months <= 1 ? "1mo ago" : `${months}mo ago`;
}

/**
 * Compact list of community signals that fall on (or near) the selected
 * route, ordered by distance from the start. Tap a row to fly the map there
 * and open the signal's popup with the actual report text.
 */
export const SignalsAlongRoute = ({ tags, routeCoords, bufferM = 120, onFocus }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const matched = useMemo(() => {
    if (routeCoords.length < 2 || tags.length === 0) return [];
    return tags
      .map((t) => {
        const snap = nearestPointOnPolyline([t.lng, t.lat], routeCoords);
        return { tag: t, distance: snap.distance, along: snap.along };
      })
      .filter((m) => m.distance <= bufferM)
      .sort((a, b) => a.along - b.along);
  }, [tags, routeCoords, bufferM]);

  if (matched.length === 0) return null;

  const positives = matched.filter((m) => m.tag.sentiment === "positive").length;
  const negatives = matched.length - positives;
  const visible = expanded ? matched : matched.slice(0, 3);

  return (
    <div className="bg-card border border-border rounded-2xl shadow-soft overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium">Community signals on this route</div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-sentiment-positive" />
              {positives} pleasant
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-sentiment-negative" />
              {negatives} unpleasant
            </span>
          </div>
        </div>
        {matched.length > 3 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            {expanded ? "Show less" : `Show all ${matched.length}`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      <ul className="divide-y divide-border">
        {visible.map(({ tag, distance }) => {
          const positive = tag.sentiment === "positive";
          return (
            <li key={tag.id}>
              <button
                type="button"
                onClick={() => onFocus(tag.id)}
                className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-smooth flex items-center gap-3"
              >
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    positive
                      ? "bg-sentiment-positive/15 text-sentiment-positive"
                      : "bg-sentiment-negative/15 text-sentiment-negative"
                  }`}
                >
                  {positive ? <ThumbsUp className="w-3.5 h-3.5" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{tag.tag_label}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span>{fmtAge(tag.created_at)}</span>
                    {tag.report_kind && (
                      <span className="capitalize">· {tag.report_kind.replace(/_/g, " ")}</span>
                    )}
                    <span>· {Math.round(distance)} m off route</span>
                  </div>
                </div>
                <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
